import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { CheckCircle } from 'lucide-react';
import { useReferralTracking } from '@/hooks/useReferralTracking';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const Success = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateReferralStatus } = useReferralTracking();

  useEffect(() => {
    const updateSubscriptionStatus = async () => {
      if (!user?.id) return;

      try {
        // Obtener el precio de la suscripción de la base de datos
        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('price_amount, stripe_subscription_id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();

        // Solo actualizar si es una suscripción válida de Stripe
        if (subscription?.stripe_subscription_id && subscription?.price_amount) {
          // Actualizar el estado del referido con el monto de la suscripción
          await updateReferralStatus(user.id, 'paid', subscription.price_amount);
        }
      } catch (error) {
        console.error('Error updating referral status:', error);
      }
    };

    // Lanzar confetti
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#34D399', '#60A5FA', '#A78BFA']
      });
      
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#34D399', '#60A5FA', '#A78BFA']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
    updateSubscriptionStatus();

    // Redirigir al dashboard después de 5 segundos
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate, user?.id, updateReferralStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mx-auto w-20 h-20 mb-6"
        >
          <CheckCircle className="w-full h-full text-green-500" />
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-3xl font-bold text-gray-900 mb-4"
        >
          ¡Bienvenido a Premium!
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-gray-600 mb-8"
        >
          Tu suscripción se ha activado correctamente. Prepárate para una experiencia de aprendizaje única.
        </motion.p>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-sm text-gray-500"
        >
          Serás redirigido al dashboard en unos segundos...
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Success;
